import React, { useState, useEffect } from 'react'
//import { useNavigate } from 'react-router-dom'

import { LogErrors } from './../../utilities/LogErrors'
import { usePersistedState } from './../../utilities/usePersistedState'

import { Loading } from './Loading'
import { Header } from './../Header/Header'
import { SortIndicator } from './Tables/SortIndicator'
import { SortHelpText } from './Tables/SortHelpText'


/**
 * Componente reutilizable para renderizar una tabla con:
 * - Acciones individuales por fila (fijas o dinámicas).
 * - Acciones masivas.
 * - Posibilidad de agregar botones en celdas específicas.
 * - Ordenamiento al pulsar los encabezados. Por defecto se ordena por la misma clave con la que
 *   se muestra cada columna (rowKeys); si una columna se muestra con una clave que no sirve para
 *   ordenar (por ejemplo, un texto armado o una celda personalizada), `sortKeys` indica la clave
 *   de cada columna que sí se debe usar para ordenar.
 */
const Tables = ({
    columns,
    rowKeys,
    sortKeys = rowKeys, // Opcional: claves para ordenar, en el mismo orden que columns
    rows,
    actions = [], // Puede ser arreglo o función: (row) => []
    bulkActions = [],
    allRows = [],
    selectionMessage,
    extraClassName,
    extraCellButtons = {}, // Nuevo: objeto con funciones por columna para agregar botón extra en celdas
    extraCellRenderers = {},
    searchQuery = '',
    sortStorageKey = null, // Opcional: si se manda, el orden elegido se conserva mientras dure la sesión
    onSelectionChange = () => {}
}) => {
    const [selectedIds, setSelectedIds] = useState([])
    const [sortConfig, setSortConfig] = usePersistedState(sortStorageKey, { key: null, direction: 'asc' })

    const isSelected = (id) => selectedIds.includes(id)

    const toggleSelect = (id) => {
        const newSelected = selectedIds.includes(id)
        ? selectedIds.filter((i) => i !== id)
        : [...selectedIds, id]

        setSelectedIds(newSelected)
        onSelectionChange(newSelected) // <-- actualiza en el componente padre
    }

    const handleBulkAction = (action) => {
        const selectedRows = allRows.filter((row) => selectedIds.includes(row.id))
        action.onClick(selectedRows)
        setSelectedIds([])
    }

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            }

            return { key, direction: 'asc' }
        })
    }

    const sortedRows = React.useMemo(() => {
        if (!sortConfig.key) return rows

        return [...rows].sort((a, b) => {
            const aVal = a[sortConfig.key]
            const bVal = b[sortConfig.key]

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortConfig.direction === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal)
            }

            return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
        })
    }, [rows, sortConfig])

    const actionCols = typeof actions === 'function' ? 1 : (actions?.length || 0)
    const totalCols = 1 + (columns?.length || 0) + actionCols // 1 = checkbox

    
    return (
        <>
        <SortHelpText extraClassName='mt-3' />

        <div className='container Tables--Shadow Tables--Background'>
            {selectedIds.length > 0 && bulkActions.length > 0 && (
                <div className='Tables__ExtraButtons'>
                {bulkActions.map((action, i) => (
                    <button type='button' key={i} onClick={() => handleBulkAction(action)} className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'>{action.label.replace('{n}', selectedIds.length)}</button>
                ))}
                </div>
            )}

            {selectedIds.length > 0 && (
                <div className='Tables__Table__RegistersSelected'>
                    <button className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm' onClick={() => setSelectedIds([])}>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                    </button>

                    <div className='Tables__Table__RegistersSelected__Text'>
                        {selectedIds.length} {selectionMessage || 'fila(s) seleccionada(s)'}
                    </div>

                    <button className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg' onClick={() => setSelectedIds([])}>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                    </button>
                </div>
            )}

            <section className='Tables__Container'>
                <table className={`Tables__Table ${extraClassName || ''}`}>
                    {selectedIds.length === 0 && (
                        <thead>
                            <tr className='Tables__Table__Row--Header'>
                                <td className={`Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn ${extraClassName && `${extraClassName}__InputColumn`}`}>
                                    <input
                                        type='checkbox'
                                        className='Tables__Table__InputColumn__Input'
                                        onChange={(e) => {
                                        const newIds = e.target.checked
                                            ? rows.map((row) => row.id)
                                            : []
                                        setSelectedIds(newIds)
                                        }}
                                        checked={rows.every((row) => selectedIds.includes(row.id)) && rows.length > 0}
                                    />
                                </td>
                                {columns.map((col, indexColumns) => (
                                <td
                                    key={indexColumns}
                                    onClick={() => handleSort(sortKeys[indexColumns])}
                                    style={{ cursor: 'pointer' }}
                                    className={`Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn ${extraClassName && `${extraClassName}__TextColumn`}`}
                                >
                                    {col}
                                    <SortIndicator sortConfig={sortConfig} columnKey={sortKeys[indexColumns]} />
                                </td>
                                ))}
                                {typeof actions === 'function'
                                    ? null // No hay forma de saber cuántas columnas poner si son dinámicas
                                    : actions.map((action, indexActionButtons) => (
                                        <td key={indexActionButtons} className={`Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn ${extraClassName && `${extraClassName}__ActionButtonsColumn`}`} />
                                ))}
                            </tr>
                        </thead>
                    )}
                    {/* <tbody>
                        {sortedRows.map((row,index) => {
                            const rowActions = typeof actions === 'function' ? actions(row) : actions

                            return (
                                <tr key={index} className='Tables__Table__Row Instructor__Agendas__ContainerTable__Table__Rows'>
                                    <td className={`Tables__Table__Row__Cell Tables__Table__InputColumn ${extraClassName && `${extraClassName}__InputColumn`}`}>
                                        <input
                                            type='checkbox'
                                            className='Tables__Table__InputColumn__Input'
                                            checked={isSelected(row.id)}
                                            onChange={() => toggleSelect(row.id)}
                                        />
                                    </td>
                                    {rowKeys.map((key, indexRowKeys) => {
                                        const renderer = extraCellRenderers && typeof extraCellRenderers[key] === 'function'
                                            ? extraCellRenderers[key]
                                            : null

                                        const content = renderer ? renderer(row) : row[key]

                                        return (
                                            <td key={indexRowKeys} className={`Tables__Table__Row__Cell Tables__Table__TextColumn ${extraClassName && `${extraClassName}__TextColumn`}`}>
                                                {React.isValidElement(content) ? content : <span>{content}</span>}
                                            </td>
                                        )
                                    })}
                                    {rowActions.map((action, indexRowActions) => (
                                        <td key={indexRowActions} className={`Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn ${extraClassName && `${extraClassName}__ActionButtonsColumn`}`}>
                                            <button onClick={() => action.onClick(row)} className='Btn__Ztudia--PrimaryOutline'>
                                                <i className={action.icon}></i> {action.label}
                                            </button>
                                        </td>
                                    ))}
                                </tr>
                            )
                        })}
                    </tbody> */}
                    <tbody>
                        {sortedRows.length === 0 ? (
                            <tr className='Tables__Table__Row'>
                                <td className='Tables__Table__Row__Cell' colSpan={totalCols}>
                                    {searchQuery
                                        ? <>No se encontraron coincidencias para “<strong>{searchQuery}</strong>”.</>
                                        : 'No hay registros para mostrar.'}
                                </td>
                            </tr>
                        ) : (
                            sortedRows.map((row,index) => {
                                const rowActions = typeof actions === 'function' ? actions(row) : actions

                                return (
                                    <tr key={index} className='Tables__Table__Row'>
                                        <td className={`Tables__Table__Row__Cell Tables__Table__InputColumn ${extraClassName && `${extraClassName}__InputColumn`}`}>
                                            <input
                                                type='checkbox'
                                                className='Tables__Table__InputColumn__Input'
                                                checked={isSelected(row.id)}
                                                onChange={() => toggleSelect(row.id)}
                                            />
                                        </td>
                                        {rowKeys.map((key, indexRowKeys) => (
                                            <td key={indexRowKeys} className={`Tables__Table__Row__Cell Tables__Table__TextColumn ${extraClassName && `${extraClassName}__TextColumn`}`}>
                                                {extraCellRenderers[key]
                                                    ? extraCellRenderers[key](row)   // <<< render personalizado
                                                    : <span>{row[key]}</span>}
                                                {extraCellButtons[key] && (
                                                    <button
                                                        type='button'
                                                        className='Tables__Table__TextColumn__ExtraButton'
                                                        onClick={() => extraCellButtons[key](row)}
                                                    >
                                                        <i className='bi bi-pencil' />
                                                    </button>
                                                )}
                                            </td>
                                        ))}
                                        {rowActions.map((action, indexRowActions) => (
                                            <td key={indexRowActions} className={`Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn ${extraClassName && `${extraClassName}__ActionButtonsColumn`}`}>
                                                <button onClick={() => action.onClick(row)} className='Btn__Ztudia--PrimaryOutline'>
                                                    <i className={action.icon}></i> {action.label}
                                                </button>
                                            </td>
                                        ))}
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </section>
        </div>
        </>
    )
}

export { Tables };












// import React, { useState, useEffect } from 'react'
// //import { useNavigate } from 'react-router-dom'

// import { LogErrors } from './../../utilities/LogErrors'

// import { Loading } from './Loading'
// import { Header } from './../Header/Header'


// /**
//  * Componente reutilizable para renderizar una tabla con:
//  * - Acciones individuales por fila (fijas o dinámicas).
//  * - Acciones masivas.
//  * - Posibilidad de agregar botones en celdas específicas.
//  */
// const Tables = ({
//     columns,
//     rowKeys,
//     rows,
//     actions = [], // Puede ser arreglo o función: (row) => []
//     bulkActions = [],
//     allRows = [],
//     selectionMessage,
//     extraClassName,
//     extraCellButtons = {}, // Nuevo: objeto con funciones por columna para agregar botón extra en celdas
//     onSelectionChange = () => {}
// }) => {
//     const [selectedIds, setSelectedIds] = useState([])
//     const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

//     const isSelected = (id) => selectedIds.includes(id)

//     const toggleSelect = (id) => {
//         const newSelected = selectedIds.includes(id)
//         ? selectedIds.filter((i) => i !== id)
//         : [...selectedIds, id]

//         setSelectedIds(newSelected)
//         onSelectionChange(newSelected) // <-- actualiza en el componente padre
//     }

//     const handleBulkAction = (action) => {
//         const selectedRows = allRows.filter((row) => selectedIds.includes(row.id))
//         action.onClick(selectedRows)
//         setSelectedIds([])
//     }

//     const handleSort = (key) => {
//         setSortConfig((prev) => {
//             if (prev.key === key) {
//                 return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
//             }

//             return { key, direction: 'asc' }
//         })
//     }

//     const sortedRows = React.useMemo(() => {
//         if (!sortConfig.key) return rows

//         return [...rows].sort((a, b) => {
//             const aVal = a[sortConfig.key]
//             const bVal = b[sortConfig.key]

//             if (typeof aVal === 'string' && typeof bVal === 'string') {
//                 return sortConfig.direction === 'asc'
//                     ? aVal.localeCompare(bVal)
//                     : bVal.localeCompare(aVal)
//             }

//             return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
//         })
//     }, [rows, sortConfig])

    
//     return (
//         <div className='container'>
//             {selectedIds.length > 0 && bulkActions.length > 0 && (
//                 <div className='Tables__ExtraButtons'>
//                 {bulkActions.map((action, i) => (
//                     <button type='button' key={i} onClick={() => handleBulkAction(action)} className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'>{action.label.replace('{n}', selectedIds.length)}</button>
//                 ))}
//                 </div>
//             )}

//             {selectedIds.length > 0 && (
//                 <div className='Tables__Table__RegistersSelected'>
//                     <button className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm' onClick={() => setSelectedIds([])}>
//                         <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
//                         <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
//                     </button>

//                     <div className='Tables__Table__RegistersSelected__Text'>
//                         {selectedIds.length} {selectionMessage || 'fila(s) seleccionada(s)'}
//                     </div>

//                     <button className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg' onClick={() => setSelectedIds([])}>
//                         <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
//                         <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
//                     </button>
//                 </div>
//             )}

//             <section className='Tables__Container'>
//                 <table className={`Tables__Table ${extraClassName || ''}`}>
//                     {selectedIds.length === 0 && (
//                         <thead>
//                             <tr className='Tables__Table__Row--Header'>
//                                 <td className={`Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn ${extraClassName && `${extraClassName}__InputColumn`}`}>
//                                     <input
//                                         type='checkbox'
//                                         className='Tables__Table__InputColumn__Input'
//                                         onChange={(e) => {
//                                         const newIds = e.target.checked
//                                             ? rows.map((row) => row.id)
//                                             : []
//                                         setSelectedIds(newIds)
//                                         }}
//                                         checked={rows.every((row) => selectedIds.includes(row.id)) && rows.length > 0}
//                                     />
//                                 </td>
//                                 {columns.map((col, indexColumns) => (
//                                 <td
//                                     key={indexColumns}
//                                     onClick={() => handleSort(rowKeys[indexColumns])}
//                                     style={{ cursor: 'pointer' }}
//                                     className={`Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn ${extraClassName && `${extraClassName}__TextColumn`}`}
//                                 >
//                                     {col}
//                                     {sortConfig.key === rowKeys[indexColumns] && <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>}
//                                 </td>
//                                 ))}
//                                 {typeof actions === 'function'
//                                     ? null // No hay forma de saber cuántas columnas poner si son dinámicas
//                                     : actions.map((action, indexActionButtons) => (
//                                         <td key={indexActionButtons} className={`Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn ${extraClassName && `${extraClassName}__ActionButtonsColumn`}`} />
//                                 ))}
//                             </tr>
//                         </thead>
//                     )}
//                     <tbody>
//                         {sortedRows.map((row,index) => {
//                             const rowActions = typeof actions === 'function' ? actions(row) : actions

//                             return (
//                                 <tr key={index} className='Tables__Table__Row'>
//                                     <td className={`Tables__Table__Row__Cell Tables__Table__InputColumn ${extraClassName && `${extraClassName}__InputColumn`}`}>
//                                         <input
//                                             type='checkbox'
//                                             className='Tables__Table__InputColumn__Input'
//                                             checked={isSelected(row.id)}
//                                             onChange={() => toggleSelect(row.id)}
//                                         />
//                                     </td>
//                                     {rowKeys.map((key, indexRowKeys) => (
//                                         <td key={indexRowKeys} className={`Tables__Table__Row__Cell Tables__Table__TextColumn ${extraClassName && `${extraClassName}__TextColumn`}`}>
//                                             <span>{row[key]}</span>
//                                             {extraCellButtons[key] && (
//                                                 <button
//                                                     type='button'
//                                                     className='Tables__Table__TextColumn__ExtraButton'
//                                                     onClick={() => extraCellButtons[key](row)}
//                                                 >
//                                                     <i className='bi bi-pencil' />
//                                                 </button>
//                                             )}
//                                         </td>
//                                     ))}
//                                     {rowActions.map((action, indexRowActions) => (
//                                         <td key={indexRowActions} className={`Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn ${extraClassName && `${extraClassName}__ActionButtonsColumn`}`}>
//                                             <button onClick={() => action.onClick(row)} className='Btn__Ztudia--PrimaryOutline'>
//                                                 <i className={action.icon}></i> {action.label}
//                                             </button>
//                                         </td>
//                                     ))}
//                                 </tr>
//                             )
//                         })}
//                     </tbody>
//                 </table>
//             </section>
//         </div>
//     )
// }

// export { Tables };